import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Clock, Calendar as CalIcon, Briefcase, Tag } from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";

const CATEGORIES = [
  { code: "administrativ", label: "Administrativ", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { code: "comercial",     label: "Comercial",     color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { code: "santier",       label: "Șantier",       color: "bg-orange-100 text-orange-800 border-orange-200" },
  { code: "it",            label: "IT / Sistem",   color: "bg-violet-100 text-violet-800 border-violet-200" },
  { code: "altele",        label: "Altele",        color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
];

const catMeta = (code) => CATEGORIES.find((c) => c.code === code) || CATEGORIES[CATEGORIES.length - 1];

// HH:MM ↔ minutes
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return 0;
  const m = s.trim().match(/^(\d{1,3}):([0-5]?\d)$/);
  if (!m) return 0;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};
const minToHHMM = (min) => {
  const total = Math.max(0, parseInt(min, 10) || 0);
  const h = Math.floor(total / 60);
  const mm = total % 60;
  return `${h}:${String(mm).padStart(2, "0")}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function TagesraportPanel({ personId, personName, canEdit = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // entry being edited or null for create
  const [busy, setBusy] = useState(false);
  // Status obligativitate (Khreis & Lorena) + zile lipsă (afișate cu roșu)
  const [missingInfo, setMissingInfo] = useState({ required: false, missing: [], today: "", mandatory_from: "" });

  // Filtru lună + scope (single-month vs all-months din iunie 2026 încoace)
  const [monthFilter, setMonthFilter] = useState(() => new Date().toISOString().slice(0, 7));
  const [scope, setScope] = useState("month"); // "month" | "all"

  const load = async () => {
    if (!personId) return;
    setLoading(true);
    try {
      let from, to;
      if (scope === "all") {
        from = "2026-06-01";  // iunie 2026 = punctul de start (când s-a introdus feature-ul)
        to = new Date().toISOString().slice(0, 10);
      } else {
        from = `${monthFilter}-01`;
        const [y, m] = monthFilter.split("-").map((x) => parseInt(x, 10));
        const lastDay = new Date(y, m, 0).getDate();
        to = `${monthFilter}-${String(lastDay).padStart(2, "0")}`;
      }
      const [rRows, rMissing] = await Promise.all([
        api.get(`/tagesraporte?person_id=${personId}&from=${from}&to=${to}`),
        api.get(`/tagesraporte/missing?person_id=${personId}`).catch(() => ({ data: null })),
      ]);
      setRows(rRows.data || []);
      if (rMissing.data) setMissingInfo(rMissing.data);
    } catch (e) {
      console.error("Tagesraport load", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, monthFilter, scope]);

  // Set cu datele lipsă vizibile în luna curentă (pentru evidențiere roșie inline)
  const missingInMonthSet = useMemo(() => {
    if (scope === "all") return new Set(missingInfo.missing || []);
    return new Set((missingInfo.missing || []).filter((d) => d.startsWith(monthFilter)));
  }, [missingInfo, monthFilter, scope]);

  // Grupare după dată (cel mai nou sus)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date).push(r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [rows]);

  const totalMinMonth = useMemo(() => rows.reduce((s, r) => s + (parseInt(r.duration_min, 10) || 0), 0), [rows]);

  const openNew = () => {
    setEditing({
      _new: true,
      person_id: personId,
      date: todayIso(),
      task: "",
      description: "",
      duration_hhmm: "1:00",
      start_time: "",
      end_time: "",
      project_id: "",
      category: "administrativ",
    });
  };
  const openNewForDate = (iso) => {
    setEditing({
      _new: true,
      person_id: personId,
      date: iso,
      task: "",
      description: "",
      duration_hhmm: "1:00",
      start_time: "",
      end_time: "",
      project_id: "",
      category: "administrativ",
    });
  };
  const openEdit = (entry) => {
    setEditing({
      ...entry,
      duration_hhmm: minToHHMM(entry.duration_min),
      start_time: entry.start_time || "",
      end_time: entry.end_time || "",
      project_id: entry.project_id || "",
    });
  };

  // Durata calculată automat din start/final (− 1h pauză de prânz)
  const autoWorkedMin = useMemo(() => {
    const st = editing?.start_time, et = editing?.end_time;
    if (!st || !et) return null;
    const [sh, sm] = st.split(":").map(Number);
    const [eh, em] = et.split(":").map(Number);
    if ([sh, sm, eh, em].some(isNaN)) return null;
    const total = eh * 60 + em - (sh * 60 + sm);
    return total > 60 ? total - 60 : null;
  }, [editing?.start_time, editing?.end_time]);

  const save = async () => {
    if (!editing) return;
    const hasTimes = editing.start_time && editing.end_time;
    const min = hasTimes ? (autoWorkedMin || 0) : hhmmToMin(editing.duration_hhmm);
    if (!editing.task.trim()) { toast.error("Specifică task-ul"); return; }
    if (!editing.date) { toast.error("Specifică data"); return; }
    if (min <= 0) { toast.error(hasTimes ? "Interval invalid — ora de final trebuie să fie după start (min. 1h peste pauză)" : "Durată invalidă — folosește format HH:MM (ex: 2:30)"); return; }
    setBusy(true);
    try {
      const payload = {
        person_id: editing.person_id,
        date: editing.date,
        task: editing.task.trim(),
        description: (editing.description || "").trim() || null,
        duration_min: min,
        start_time: hasTimes ? editing.start_time : null,
        end_time: hasTimes ? editing.end_time : null,
        project_id: editing.project_id || null,
        category: editing.category || "administrativ",
      };
      if (editing._new) {
        await api.post("/tagesraporte", payload);
        toast.success("Raport adăugat");
      } else {
        await api.put(`/tagesraporte/${editing.id}`, payload);
        toast.success("Raport actualizat");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const del = async (entry) => {
    if (!window.confirm(`Șterge raportul „${entry.task}"?`)) return;
    try {
      await api.delete(`/tagesraporte/${entry.id}`);
      toast.success("Șters");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  return (
    <div className="space-y-3" data-testid="tagesraport-panel">
      {/* Header cu filtru lună + sumar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalIcon size={16} className="text-zinc-500"/>
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setScope("month")}
              className={`px-2.5 py-1.5 font-semibold transition-colors ${scope === "month" ? "bg-cyan-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="tagesraport-scope-month"
            >
              Lună
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              className={`px-2.5 py-1.5 font-semibold transition-colors ${scope === "all" ? "bg-cyan-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
              data-testid="tagesraport-scope-all"
              title="Toate rapoartele din iunie 2026 până azi"
            >
              Toate (din iun 2026)
            </button>
          </div>
          {scope === "month" && (
            <Input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="h-9 w-[150px] text-sm"
              data-testid="tagesraport-month"
            />
          )}
          <div className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900 font-bold tabular-nums">
            Total: <span data-testid="tagesraport-month-total">{minToHHMM(totalMinMonth)}</span>
          </div>
        </div>
        {canEdit && (
          <Button onClick={openNew} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="tagesraport-new-btn">
            <Plus size={14} className="mr-1.5"/> Adaugă raport
          </Button>
        )}
      </div>

      {/* Banner OBLIGATIVITATE — afișează zilele lipsă (în roșu) doar dacă persoana e obligată */}
      {missingInfo.required && missingInfo.missing && missingInfo.missing.length > 0 && (
        <div className="rounded-lg border-2 border-red-400 bg-red-50 p-3 shadow-sm" data-testid="tagesraport-missing-banner">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              <div>
                <div className="font-display font-extrabold text-red-900 text-sm uppercase tracking-wide">
                  {missingInfo.missing.length} {missingInfo.missing.length === 1 ? "zi lipsă" : "zile lipsă"} — completează raportul
                </div>
                <div className="text-[11px] text-red-700">
                  Obligatoriu zilnic începând cu <strong>{(missingInfo.mandatory_from || "").split("-").reverse().join(".")}</strong>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {missingInfo.missing.slice(-30).reverse().map((d) => {
              const ymd = d.split("-");
              const dd = ymd[2], mm = ymd[1];
              const isToday = d === missingInfo.today;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => { if (canEdit) openNewForDate(d); }}
                  disabled={!canEdit}
                  className={`px-2 py-1 rounded text-[11px] font-bold tabular-nums border-2 transition-colors ${isToday ? "border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-200" : "border-red-500 bg-red-100 text-red-900 hover:bg-red-200"} ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                  data-testid={`tagesraport-missing-day-${d}`}
                  title={canEdit ? `Click pentru a completa raportul pentru ${dd}.${mm}` : "Lipsă"}
                >
                  {isToday ? "🕐 AZI" : `${dd}.${mm}`}
                </button>
              );
            })}
            {missingInfo.missing.length > 30 && (
              <span className="text-[11px] text-red-700 self-center italic">… și încă {missingInfo.missing.length - 30} zile mai vechi</span>
            )}
          </div>
        </div>
      )}

      {/* Listă pe zile */}
      {loading ? (
        <div className="text-sm text-zinc-500 italic py-6 text-center">Se încarcă...</div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-zinc-500 italic py-8 text-center border border-dashed rounded-md" data-testid="tagesraport-empty">
          Nicio intrare pentru {personName} în această lună. Apasă „Adaugă raport" ca să începi.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(([date, entries]) => {
            const total = entries.reduce((s, e) => s + (parseInt(e.duration_min, 10) || 0), 0);
            return (
              <div key={date} className="rounded-lg border-2 border-zinc-200 bg-white overflow-hidden" data-testid={`tagesraport-day-${date}`}>
                <div className="px-3 py-2 bg-zinc-50 border-b flex items-center justify-between gap-2">
                  <div className="font-bold text-zinc-800 flex items-center gap-2">
                    <CalIcon size={14} className="text-zinc-500"/>
                    {fmtRoDate(date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{entries.length} task{entries.length === 1 ? "" : "uri"}</span>
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-bold tabular-nums">⏱ {minToHHMM(total)}</span>
                  </div>
                </div>
                <ul className="divide-y">
                  {entries.map((e) => {
                    const cm = catMeta(e.category);
                    return (
                      <li key={e.id} className="px-3 py-2 hover:bg-zinc-50/60 flex items-start gap-3" data-testid={`tagesraport-entry-${e.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cm.color}`}>{cm.label}</span>
                            <span className="text-sm font-semibold text-zinc-900 truncate">{e.task}</span>
                            <span className="ml-auto text-[12px] font-bold tabular-nums text-emerald-700 flex items-center gap-1">
                              <Clock size={11}/> {minToHHMM(e.duration_min)}{e.start_time && e.end_time ? ` (${e.start_time}–${e.end_time}, −1h pauză)` : ""}
                            </span>
                          </div>
                          {e.description && (
                            <div className="text-[12px] text-zinc-600 mt-1 break-words">{e.description}</div>
                          )}
                          {e.project_label && (
                            <div className="text-[11px] text-blue-700 mt-1 flex items-center gap-1">
                              <Briefcase size={11}/> {e.project_label}
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEdit(e)}
                              className="p-1 rounded hover:bg-zinc-200 text-zinc-600"
                              title="Editează"
                              data-testid={`tagesraport-edit-${e.id}`}
                            >
                              <Edit2 size={13}/>
                            </button>
                            <button
                              type="button"
                              onClick={() => del(e)}
                              className="p-1 rounded hover:bg-red-50 text-red-600"
                              title="Șterge"
                              data-testid={`tagesraport-del-${e.id}`}
                            >
                              <Trash2 size={13}/>
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog: Add / Edit */}
      {editing && (
        <Dialog open onOpenChange={(v) => { if (!v && !busy) setEditing(null); }}>
          <DialogContent className="max-w-lg" data-testid="tagesraport-dialog">
            <DialogHeader>
              <DialogTitle>{editing._new ? "Adaugă raport zilnic" : "Editează raport"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-label flex items-center gap-1">📅 Data <span className="text-red-600">*</span></Label>
                  <Input
                    type="date"
                    value={editing.date}
                    max={todayIso()}
                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    className="mt-1"
                    data-testid="tagesraport-date"
                  />
                </div>
                <div>
                  <Label className="small-label flex items-center gap-1"><Clock size={11}/> Durată (HH:MM) {!editing.start_time && !editing.end_time && <span className="text-red-600">*</span>}</Label>
                  <Input
                    type="text"
                    value={editing.start_time && editing.end_time && autoWorkedMin ? minToHHMM(autoWorkedMin) : editing.duration_hhmm}
                    placeholder="2:30"
                    disabled={!!(editing.start_time && editing.end_time)}
                    onChange={(e) => setEditing({ ...editing, duration_hhmm: e.target.value })}
                    className="mt-1 font-mono"
                    data-testid="tagesraport-duration"
                  />
                </div>
              </div>
              <div className="border border-blue-200 bg-blue-50 rounded-md p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-800 mb-2">⏰ Sau completează orele de lucru (pauza de prânz de 1h se scade automat)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="small-label">Ora start</Label>
                    <Input type="time" value={editing.start_time || ""} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} className="mt-1" data-testid="tagesraport-start-time" />
                  </div>
                  <div>
                    <Label className="small-label">Ora final</Label>
                    <Input type="time" value={editing.end_time || ""} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} className="mt-1" data-testid="tagesraport-end-time" />
                  </div>
                </div>
                {editing.start_time && editing.end_time && (
                  <div className="mt-2 text-xs font-semibold text-blue-900" data-testid="tagesraport-computed">
                    {autoWorkedMin
                      ? <>Interval {editing.start_time}–{editing.end_time} − 1h pauză = <strong>{minToHHMM(autoWorkedMin)} ore lucrate</strong></>
                      : <span className="text-red-600">Interval prea scurt (minim 1h peste pauza de prânz)</span>}
                  </div>
                )}
              </div>
              <div>
                <Label className="small-label flex items-center gap-1">Task <span className="text-red-600">*</span></Label>
                <Input
                  value={editing.task}
                  onChange={(e) => setEditing({ ...editing, task: e.target.value })}
                  placeholder="ex: Discuții ofertă client X"
                  className="mt-1"
                  data-testid="tagesraport-task"
                />
              </div>
              <div>
                <Label className="small-label">Descriere (opțional)</Label>
                <Textarea
                  rows={3}
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Detalii / context — opțional"
                  className="mt-1 text-sm"
                  data-testid="tagesraport-desc"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-label flex items-center gap-1"><Tag size={11}/> Categorie</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="tagesraport-category"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="small-label flex items-center gap-1"><Briefcase size={11}/> Proiect (opțional)</Label>
                  <Select value={editing.project_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, project_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="tagesraport-project"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__none__">— Niciun proiect —</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name || p.address || p.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Anulează</Button>
              <Button
                onClick={save}
                disabled={busy || !editing.task?.trim() || hhmmToMin(editing.duration_hhmm) <= 0 || !editing.date}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="tagesraport-save"
              >
                {busy ? "Se salvează..." : "Salvează"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
